import React from 'react';

// Helper function to parse inline markdown
const parseInlineMarkdown = (text) => {
  // Replace bold (**text**)
  text = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  
  // Replace italic (*text* or _text_)
  text = text.replace(/(?<!\*)\*(?!\*)(.*?)(?<!\*)\*(?!\*)/g, '<em>$1</em>');
  text = text.replace(/(?<!_)_(?!_)(.*?)(?<!_)_(?!_)/g, '<em>$1</em>');
  
  // Replace strikethrough (~~text~~)
  text = text.replace(/~~(.*?)~~/g, '<s>$1</s>');
  
  // Replace underline (__text__)
  text = text.replace(/__(.*?)__/g, '<u>$1</u>');
  
  // Replace spoiler (||text||)
  text = text.replace(/\|\|(.*?)\|\|/g, '<span class="bg-black text-black hover:text-white transition-colors duration-200 cursor-pointer">$1</span>');
  
  // Replace code (`text`)
  text = text.replace(/`(.*?)`/g, '<code class="bg-gray-100 px-1 py-0.5 rounded">$1</code>');
  
  // Replace links [text](url)
  text = text.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" class="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">$1</a>');
  
  return text;
};

export const parseMarkdownWithImages = (content, images = []) => {
  // console.log('Starting markdown parsing with:', { content, images });
  
  if (!content) {
    // console.log('No content provided, returning empty result');
    return { content: null, unusedImages: [] };
  }

  try {
    // Split content into lines
    const lines = content.split('\n');
    // console.log('Split into lines:', lines);
    
    const parsedContent = [];
    const usedImageIndices = new Set();
    let currentListItems = [];
    let inList = false;
    let inQuote = false;
    let inNote = false;
    let inCodeBlock = false;
    let currentQuote = [];
    let currentNote = [];
    let currentCodeBlock = [];
    let codeBlockLanguage = '';

    const processList = () => {
      if (currentListItems.length > 0) {
        parsedContent.push(
          <ul key={`list-${parsedContent.length}`} className="list-disc pl-6 mb-4">
            {currentListItems.map((item, index) => (
              <li 
                key={index} 
                className="mb-2"
                dangerouslySetInnerHTML={{ __html: parseInlineMarkdown(item) }}
              />
            ))}
          </ul>
        );
        currentListItems = [];
      }
    };

    const processQuote = () => {
      if (currentQuote.length > 0) {
        parsedContent.push(
          <blockquote 
            key={`quote-${parsedContent.length}`} 
            className="border-l-4 border-gray-300 pl-4 my-4 italic text-gray-700"
          >
            {currentQuote.map((line, index) => (
              <p 
                key={index} 
                className="mb-2"
                dangerouslySetInnerHTML={{ __html: parseInlineMarkdown(line) }}
              />
            ))}
          </blockquote>
        );
        currentQuote = [];
      }
    };

    const processNote = () => {
      if (currentNote.length > 0) {
        const firstLine = currentNote[0];
        let title = 'Note';
        let content = currentNote;
        
        // Extract title from the first line if it contains a colon
        const colonIndex = firstLine.indexOf(':');
        if (colonIndex !== -1) {
          title = firstLine.substring(0, colonIndex).trim();
          content = [firstLine.substring(colonIndex + 1).trim(), ...currentNote.slice(1)];
        }
        
        parsedContent.push(
          <div 
            key={`note-${parsedContent.length}`} 
            className="bg-blue-50 border-l-4 border-blue-500 p-4 my-4 rounded-r-lg"
          >
            <div className="font-semibold text-blue-700 mb-2">{title}</div>
            {content.map((line, index) => (
              <p 
                key={index} 
                className="mb-2 text-blue-900"
                dangerouslySetInnerHTML={{ __html: parseInlineMarkdown(line) }}
              />
            ))}
          </div>
        );
        currentNote = [];
      }
    };

    const processCodeBlock = () => {
      if (currentCodeBlock.length > 0) {
        parsedContent.push(
          <pre 
            key={`code-${parsedContent.length}`} 
            className={`bg-gray-100 p-4 my-4 rounded-lg overflow-x-auto ${
              codeBlockLanguage ? `language-${codeBlockLanguage}` : ''
            }`}
          >
            <code>{currentCodeBlock.join('\n')}</code>
          </pre>
        );
        currentCodeBlock = [];
        codeBlockLanguage = '';
      }
    };

    lines.forEach((line, index) => {
      // Check for horizontal rule
      if (line.trim() === '---') {
        processList();
        processQuote();
        processNote();
        processCodeBlock();
        inList = false;
        inQuote = false;
        inNote = false;
        inCodeBlock = false;
        
        parsedContent.push(
          <hr key={`hr-${index}`} className="my-8 border-t border-gray-300" />
        );
        return;
      }

      // Check for image placeholders
      const imagePlaceholders = line.match(/\[image(\d+)\]/g) || [];
      
      if (imagePlaceholders.length > 0) {
        processList();
        processQuote();
        processNote();
        processCodeBlock();
        inList = false;
        inQuote = false;
        inNote = false;
        inCodeBlock = false;

        // Split line by image placeholders
        const parts = line.split(/(\[image\d+\])/);
        
        parts.forEach((part, partIndex) => {
          if (part.match(/\[image(\d+)\]/)) {
            const imageIndex = parseInt(part.match(/\[image(\d+)\]/)[1]);
            // console.log(`Found image placeholder at index ${imageIndex}`);
            
            // Handle featured image (index 0) and extra images (index 1+)
            if (imageIndex === 0 && images[0]) {
              usedImageIndices.add(0);
              parsedContent.push(
                <div key={`image-${index}-${partIndex}`} className="my-8">
                  <img 
                    src={images[0]} 
                    alt="Featured image"
                    className="max-h-[300px] w-auto mx-auto object-contain rounded-lg shadow-lg"
                    onError={(e) => {
                      console.error('Featured image failed to load:', images[0]);
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              );
            } else if (imageIndex > 0 && images[imageIndex]) {
              usedImageIndices.add(imageIndex);
              parsedContent.push(
                <div key={`image-${index}-${partIndex}`} className="my-8">
                  <img 
                    src={images[imageIndex]} 
                    alt={`Extra image ${imageIndex}`}
                    className="max-h-[300px] w-auto mx-auto object-contain rounded-lg shadow-lg"
                    onError={(e) => {
                      console.error('Extra image failed to load:', images[imageIndex]);
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              );
            } else {
              console.warn(`Invalid image index ${imageIndex} or missing image`);
            }
          } else if (part.trim()) {
            parsedContent.push(
              <p 
                key={`text-${index}-${partIndex}`} 
                className="mb-4"
                dangerouslySetInnerHTML={{ __html: parseInlineMarkdown(part) }}
              />
            );
          }
        });
      } else {
        // Check for code block start
        if (line.trim().startsWith('```')) {
          if (!inCodeBlock) {
            processList();
            processQuote();
            processNote();
            inList = false;
            inQuote = false;
            inNote = false;
            inCodeBlock = true;
            codeBlockLanguage = line.trim().substring(3).trim() || '';
          } else {
            processCodeBlock();
            inCodeBlock = false;
          }
          return;
        }

        if (inCodeBlock) {
          currentCodeBlock.push(line);
          return;
        }

        // Check for quote start
        if (line.trim().startsWith('> ')) {
          if (!inQuote) {
            processList();
            processNote();
            inList = false;
            inNote = false;
            inQuote = true;
          }
          currentQuote.push(line.substring(2));
          return;
        }
        
        // Check for note start
        if (line.trim().startsWith('!> ')) {
          if (!inNote) {
            processList();
            processQuote();
            inList = false;
            inQuote = false;
            inNote = true;
          }
          currentNote.push(line.substring(3));
          return;
        }

        // Process any pending blocks
        if (inQuote) {
          processQuote();
          inQuote = false;
        }
        if (inNote) {
          processNote();
          inNote = false;
        }

        // Check for lists
        if (line.trim().startsWith('- ') || line.trim().startsWith('* ')) {
          inList = true;
          currentListItems.push(line.trim().substring(2));
        }
        // Regular paragraph
        else if (line.trim()) {
          processList();
          inList = false;
          parsedContent.push(
            <p 
              key={`text-${index}`} 
              className="mb-4"
              dangerouslySetInnerHTML={{ __html: parseInlineMarkdown(line) }}
            />
          );
        }
        // Empty line
        else {
          processList();
          processQuote();
          processNote();
          processCodeBlock();
          inList = false;
          inQuote = false;
          inNote = false;
          inCodeBlock = false;
        }
      }
    });

    // Process any remaining blocks
    processList();
    processQuote();
    processNote();
    processCodeBlock();

    // Get unused images
    const unusedImages = images.filter((image, index) => !usedImageIndices.has(index) && image);
    // console.log('Unused images:', unusedImages);
    // console.log('Final parsed content:', parsedContent);

    return {
      content: parsedContent,
      unusedImages
    };
  } catch (error) {
    console.error('Error parsing markdown:', error);
    return {
      content: <p className="mb-4">{content}</p>,
      unusedImages: images.filter(Boolean)
    };
  }
}; 